import React, { useState, useMemo, type CSSProperties } from "react";

import { toast } from "react-hot-toast";
import {
  Menu as MenuIcon,
  X as CloseIcon,
  LayoutDashboard,
  TrendingUp,
  Percent,
  FileBarChart,
  Boxes,
  Users,
  BadgeCheck,
  Settings,
  LogOut,
} from "lucide-react";

import { api } from "../../lib/api";
import { isPro } from "../../lib/license";

const handleUpgrade = async () => {
  const userId = localStorage.getItem("userId"); // avem ID-ul userului
  if (!userId) {
    alert("User invalid!");
    return;
  }

  const response = await api("/api/StripeCheckout", {
    method: "POST",
    body: JSON.stringify({
      userId,
      priceId: "price_1SVVLKLzxL1rZrebY7YU5elv", // aici pui price-ul tău real de la Stripe
    }),
  });

  if (response?.url) {
    window.location.href = response.url; // 🔥 du-l în Stripe!
  } else {
    alert("Eroare: nu s-a putut iniția plata!");
  }
};

type LayoutBackOfficeProps = {
  title?: string;
  children: React.ReactNode;
  onExportExcel: () => void;
  onExportPDFLight: () => void;
  onExportPDFDark: () => void;
};

export default function LayoutBackOffice({
  title = "",
  children,
  onExportExcel,
  onExportPDFLight,
  onExportPDFDark,
}: LayoutBackOfficeProps) {
  const [open, setOpen] = useState(false);

  const username = localStorage.getItem("username") || "user1";
  const initials = useMemo(() => {
    const s = username.trim();
    if (!s) return "U";
    const parts = s.split(/[.\s_@-]+/).filter(Boolean);
    return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
  }, [username]);

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    setOpen(false);
    const t = document.querySelector(href);
    if (t) t.scrollIntoView({ behavior: "smooth" });
  };

  const navItems = [
    { name: "Dashboard", icon: <LayoutDashboard size={18} />, href: "#top" },
    { name: "Revenue", icon: <TrendingUp size={18} />, href: "#revenue" },
    { name: "Occupancy", icon: <Percent size={18} />, href: "#occupancy" },
    { name: "Reports", icon: <FileBarChart size={18} />, href: "#reports" },
    { name: "Inventory", icon: <Boxes size={18} />, href: "#inventory" },
    { name: "Users", icon: <Users size={18} />, href: "#users" },
    { name: "Licenses", icon: <BadgeCheck size={18} />, href: "#licenses" },
    { name: "Settings", icon: <Settings size={18} />, href: "#settings" },
  ];

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("username");
    toast.success("Logged out successfully");
    setTimeout(() => (window.location.href = "/login"), 800);
  };

  const flexRowStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  };

  const buttonStyle: CSSProperties = {
    color: "white",
    border: "none",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    minWidth: 80,
    whiteSpace: "nowrap",
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside
        className={`fixed z-20 inset-y-0 left-0 w-64 transform bg-white border-r shadow-lg transition-transform duration-200 ease-in-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h1 className="font-bold text-lg text-gray-700">HotelVPF</h1>
          <button
            className="p-2 text-gray-600 hover:text-gray-900"
            onClick={() => setOpen(false)}
          >
            <CloseIcon />
          </button>
        </div>

        <nav className="mt-4">
          {navItems.map((item) => (
            <a
              key={item.name}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-200"
            >
              {item.icon}
              {item.name}
            </a>
          ))}

          <div className="mt-2 border-t pt-2">
            <button
              onClick={() => {
                setOpen(false);
                handleLogout();
              }}
              className="flex items-center gap-3 w-full px-4 py-2 text-red-600 hover:bg-red-50"
              title="Logout"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </nav>
      </aside>

      {/* Main + header */}
      <div className="flex-1 flex flex-col w-0">
        <header className="flex items-center justify-between bg-white p-4 border-b shadow">
          {/* Left: burger + brand */}
          <div className="flex items-center gap-3">
            <button
              className="p-2 text-gray-600 hover:text-gray-900"
              onClick={() => setOpen(!open)}
              title="Menu"
            >
              {open ? <CloseIcon /> : <MenuIcon />}
            </button>

            {/* Logo + label */}
            <div className="flex items-center gap-2 group">
              <img
                src="/logos/hotelvpf-tran-logo.png"
                alt="BackOffice"
                width={45}
                height={45}
                className="block transition-all duration-300 drop-shadow-[0_0_6px_rgba(56,189,248,0.4)] group-hover:drop-shadow-[0_0_12px_rgba(56,189,248,0.7)]"
              />
              <span
                className="px-3 py-1 text-sm font-medium rounded-full 
             text-sky-700 bg-sky-50 border border-sky-100
             transition-all duration-300
             group-hover:shadow-[0_0_6px_rgba(56,189,248,0.5)]"
              >
                BackOffice
              </span>
            </div>
          </div>

          {/* Right: export buttons + user + logout */}
          <div className="flex flex-wrap items-center justify-end gap-3 w-full">
            {/* Export buttons */}
            <div style={{ ...flexRowStyle, gap: 6 }}>
              <button
                onClick={isPro() ? onExportExcel : undefined}
                style={{
                  ...buttonStyle,
                  background: isPro() ? "#22c55e" : "#9ca3af",
                  cursor: isPro() ? "pointer" : "not-allowed",
                }}
                disabled={!isPro()}
              >
                📊 Excel
              </button>

              <button
                onClick={isPro() ? onExportPDFLight : undefined}
                style={{
                  ...buttonStyle,
                  background: isPro() ? "#2563eb" : "#9ca3af",
                  cursor: isPro() ? "pointer" : "not-allowed",
                }}
                disabled={!isPro()}
              >
                📄 PDF (Light)
              </button>

              <button
                onClick={isPro() ? onExportPDFDark : undefined}
                style={{
                  ...buttonStyle,
                  background: isPro() ? "#0f172a" : "#9ca3af",
                  cursor: isPro() ? "pointer" : "not-allowed",
                }}
                disabled={!isPro()}
              >
                🌙 PDF (Dark)
              </button>

              {/* 🔥 Nou: Upgrade PRO */}
              <button
                onClick={!isPro() ? handleUpgrade : undefined}
                style={{
                  ...buttonStyle,
                  background: isPro() ? "#4ade80" : "#d97706",
                }}
                disabled={isPro()}
              >
                {isPro() ? "🌟 PRO Active" : "🚀 Upgrade PRO"}
              </button>
            </div>

            {/* User section */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex flex-col items-end leading-tight">
                <span className="block text-sm font-medium text-gray-800">
                  {username}
                </span>
                <span className="text-xs text-gray-500">Signed in</span>
              </div>

              <div
                title={username}
                className="w-9 h-9 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center shadow-md shrink-0"
              >
                {initials}
              </div>

              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-2 rounded-md bg-red-500 text-white px-3 py-1.5 hover:bg-red-600 shrink-0"
                title="Logout"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </header>

        <main className="p-6 overflow-auto flex-1">
          <span id="top" />
          {children}
        </main>
      </div>
    </div>
  );
}
